import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { TypographyStyles } from '@/styles'
import { Alert, Button, Card, CardColors, Dialog, List, Text } from '@/ui'

import { getCurrentUser } from '@/selectors/user.selectors'
import { selectHeardProductSubscription } from '@/reducers/subscription.slice'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { submitCancellationRequest } from '@/actions/settings/billingActions'
import { updateMembership } from '@/actions/userActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { USER_TRANSACTION_KEY } from '@/features/transactions/transactions.slice'
import { ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY } from '@/features/taxes/quarterly-tax-estimates/userTaxEstimates.slice'
import { selectUserHasActiveAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'

// TODO: Migrate to V2
import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { selectIsPayrollActive } from '@/featuresV1/Payroll/payroll.selectors'
import { hasTaxfyleJobOrExtensionInProgress } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.selector'

export const ConfirmManualCancellationDialog = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const currentUser = useReselector(getCurrentUser)
  const subscription = useReselector(selectHeardProductSubscription)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const hasTaxfyleJob = useReselector(
    hasTaxfyleJobOrExtensionInProgress,
    currentTaxYear
  )

  const isLoadingTransactionsAndUserTaxEstimates = useReselector(
    selectIsFetchingForKeys,
    [USER_TRANSACTION_KEY, ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY]
  )

  const hasActiveAnnualCommitment = useReselector(
    selectUserHasActiveAnnualCommitment
  )

  const isActiveGEPUser = useReselector(selectIsPayrollActive)

  useEffect(() => {
    pageView('confirm cancellation modal')
  }, [pageView])

  const cancelRequest = async () => {
    setDisabled(true)
    setLoading(true)
    const res = await submitCancellationRequest(currentUser?.id)(dispatch)
    track('submit manual cancellation request')

    if (res) {
      setDisabled(false)
      setLoading(false)

      await updateMembership(currentUser?.id, {
        manualCancellationRequestedAt: DateTime.now().toString(),
      })(dispatch)

      sendUserCancellationMessageZap(currentUser, 'manual_cancel')
      close()
      location.reload()
    } else {
      setError('We were unable to submit your cancellation request.')
      setDisabled(false)
      setLoading(false)
    }
  }

  const endDate = subscription?.nextBillingDate
    ? DateTime.fromSeconds(subscription?.nextBillingDate).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_SHORT
      )
    : 'the end of your billing period'

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button
          variant="link"
          onClick={() => {
            sendUserCancellationMessageZap(currentUser, 'clicked_canceled')
            track('click cancel plan')
          }}
          disabled={isLoadingTransactionsAndUserTaxEstimates}
        >
          Cancel Plan
        </Button>
      </Dialog.Trigger>

      <Dialog.Content>
        <Dialog.Title>
          Please note: We cannot process your cancellation automatically
        </Dialog.Title>

        {error && <Alert variant="error" description={error} />}

        <Dialog.Description>
          {hasTaxfyleJob && (
            <>
              <Text variant={TypographyStyles.BODY_LG}>
                Since we have already started filing your {currentTaxYear}{' '}
                taxes, we cannot automatically cancel your subscription until
                your taxes are transmitted to the IRS. You have until {endDate}{' '}
                to get your taxes fully filed before your next renewal.
              </Text>

              <br />

              <Text variant={TypographyStyles.BODY_LG}>
                If you want to continue, you can submit a request to cancel your
                subscription.
              </Text>
            </>
          )}

          {isActiveGEPUser && (
            <Text variant={TypographyStyles.BODY_LG}>
              If you want to continue, you can submit a request to cancel your
              subscription.
            </Text>
          )}

          {hasActiveAnnualCommitment && (
            <>
              <Text variant={TypographyStyles.BODY_LG}>
                Your contract includes an annual commitment to Heard.
              </Text>

              <Card color={CardColors.WARNING}>
                <Text className="bold">What does this mean?</Text>

                <List>
                  <List.Item>
                    <li>Refunds are not available.</li>
                  </List.Item>

                  <List.Item>
                    <li>
                      You may owe the remaining balance of your subscription.
                    </li>
                  </List.Item>
                </List>
              </Card>

              <Text variant={TypographyStyles.BODY_LG}>
                If you want to continue, you can submit a request to cancel your
                subscription.
              </Text>
            </>
          )}
        </Dialog.Description>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary">Close</Button>
          </Dialog.Close>

          <Button
            variant="primary"
            onClick={cancelRequest}
            disabled={disabled}
            loading={loading}
          >
            Submit Request
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
